import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { X, Save, Search, UserPlus, ChevronDown, Lock } from 'lucide-react';

const SEGMENTO_OPTIONS = [
    'Residência',
    'Mercado / Supermercado',
    'Padaria / Confeitaria',
    'Bar / Botequim',
    'Restaurante / Lanchonete',
    'Sorveteria',
    'Farmácia / Drogaria',
    'Academia / Esporte',
    'Salão de Beleza / Barbearia',
    'Posto de Combustível',
    'Escritório / Consultório',
    'Clínica / Hospital',
    'Escola / Colégio',
    'Hotel / Pousada',
    'Loja de Roupas / Moda',
    'Pet Shop',
    'Atacadista / Distribuidor',
    'Distribuidora de Bebidas',
    'Indústria',
    'Condomínio',
    'Comércio em Geral',
    'Outro',
];
import { supabase } from '../../src/lib/supabase';
import { UnidadeConsumidora } from '../../types';
import { ucCodigoKeys } from '../../src/lib/ucCodigo';
import { buscarUcComMesmoNumero, descreverUcExistente, mensagemUcDuplicada } from '../../src/lib/ucDuplicada';
import type { UcExistente } from '../../src/lib/ucDuplicada';
import { solicitarTrocaDonoUc, validarJustificativaTroca } from '../../src/lib/trocaDonoUc';
import { AvisoUcExistente } from '../../src/components/TrocaDonoAviso';
import QuickCreateClientModal from './QuickCreateClientModal';
import { logNegotiationAction } from '../../src/lib/negotiationLogger';
import { podeEditarCadastroDaUc, semCamposCadastrais, ucComCadastroTravado, MENSAGEM_CADASTRO_TRAVADO } from '../../src/lib/permissaoCadastro';
import { camposAlterados, comMotivo, erroColunaMotivoAusente, type CampoAlterado } from '../../src/lib/alteracoesCadastro';
import MotivoAlteracaoModal from '../../src/components/MotivoAlteracaoModal';
import PagadorOutroNomeModal from './PagadorOutroNomeModal';
import { buscarPagadorDaUc, contarUcsDoPagador } from '../../src/services/pagadores';
import { pagadorComOutroNome, type PagadorUc } from '../../src/lib/pagadorUc';
import { podeEditarCadastro } from '../../src/lib/permissaoCadastro';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { checkAndRequestDiscountApproval } from '../../src/lib/discountApproval';
import { condicaoComercialMudou, fracaoParaPercentual } from '../../src/lib/condicaoComercialMudou';
import { modalidadePorPerfil, ModalidadePlano, MODALIDADES_VALIDAS } from '../../src/lib/politicaComercial';
import { recalcularCondicaoComercial, carregarModalidadePlano } from '../../src/lib/politicaComercialDb';
import {
    avaliarImpactoEdicao,
    buscarStatusComissao,
    exigeJustificativa,
    justificativaValida,
    type ImpactoEdicaoConsumo,
} from '../../src/lib/edicaoConsumoUc';
import { resolverPlanoAtivo } from '../../src/components/PlanoMultiplicadoresTable';
import PlanoResumoCard, { type ModalidadeVenda } from '../../src/components/PlanoResumoCard';
import HistoricoDescontoUc from '../../src/components/HistoricoDescontoUc';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { temDepartamento } from '../../src/lib/departamentos';

interface UnidadeModalProps {
    isOpen: boolean;
    onClose: () => void;
    unidade?: UnidadeConsumidora | null;
    initialNegotiation?: { id: number, cliente_nome: string };
    /** Valores iniciais ao criar (unidade == null). Descontos devem vir em percentual de exibição (ex: 20, 50). */
    prefill?: Partial<UnidadeConsumidora> | null;
    onSave: () => void;
}

/** Rótulos de `consumo_medio_origem` para a tela. */
const ROTULO_ORIGEM_CONSUMO: Record<string, string> = {
    vendedor: 'estimativa do vendedor',
    analise: 'validação de dados',
    manual: 'edição manual',
    observado: 'adotado das faturas',
};

const UnidadeForm: React.FC<UnidadeModalProps> = ({ isOpen, onClose, unidade, initialNegotiation, prefill, onSave }) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();

    const canConsultCpfCnpj = profile?.role === 'adm' || profile?.role === 'gerente' || profile?.role === 'analista';
    // Só adm escolhe/reatribui a empresa faturadora da UC — demais roles nunca veem o seletor.
    const canEscolherEmpresaFaturadora = profile?.role === 'adm';
    // % da Vigor na UC de parceira: Financeiro da Vigor ou adm. O Financeiro da
    // própria parceira não mexe no próprio repasse. O banco reforça a mesma regra
    // (trg_uc_percentual_vigor_quem_altera).
    const canDefinirPercentualVigor = profile?.role === 'adm'
        || (temDepartamento(profile, 'Financeiro') && !profile?.empresa_parceira_id);
    // #6239: UC em operação só tem os dados cadastrais alterados pelo Financeiro
    // ou por adm. O banco barra do mesmo jeito (trg_protege_dados_cadastrais_uc);
    // aqui os campos já aparecem travados. Cadastro novo não trava.
    const cadastroTravado = !!unidade && !podeEditarCadastroDaUc(profile, unidade);
    const [formData, setFormData] = useState<Partial<UnidadeConsumidora>>({
        numero_uc: '',
        nome_cliente: '',
        negociacao_id: undefined,
        endereco: '',
        // Situação só é definida quando o rateio confirma a UC (sync_uc_from_rateio);
        // cadastro manual não implica que ela está de fato recebendo energia.
        situacao: null,
        status_financeiro: 'Não se aplica',
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        quadra: '',
        lote: '',
        bairro: '',
        cidade: '',
        estado: '',
        desconto_tarifa: undefined,
        desconto_bandeira: undefined,
        carencia: '',
        consumo_medio_kwh: undefined,
        valor_tarifa: undefined,
        titular_fatura: '',
        cpf_cnpj_titular: '',
        data_nascimento_titular: '',
        cpf_cnpj_cliente: '',
        data_nascimento_cliente: '',
        whatsapp_cliente: '',
        email_cliente: '',
        outro_email: '',
        observacoes_uc: '',
        num_medidor: ''
    });
    const [loading, setLoading] = useState(false);
    /** Efeito simulado da edição do consumo, aguardando confirmação do operador. */
    const [impactoEdicao, setImpactoEdicao] = useState<ImpactoEdicaoConsumo | null>(null);
    const [justificativaEdicao, setJustificativaEdicao] = useState('');
    /** Vira true depois do "Confirmar" — evita simular de novo e cair em laço. */
    const [impactoConfirmado, setImpactoConfirmado] = useState(false);
    // Mudança de cadastro ou desconto em UC em operação pede motivo antes de gravar:
    // o Faturamento vê no cálculo e na emissão (src/lib/alteracoesCadastro.ts).
    // Ref, não state: o submit seguinte lê logo depois de confirmar.
    const [pedidoMotivo, setPedidoMotivo] = useState<CampoAlterado[] | null>(null);
    const motivoConfirmadoRef = useRef<string | null>(null);
    // #6565: salvou o cliente com outro nome e o pagador (mesmo CPF/CNPJ) ficou com o antigo.
    const [pagadorOutroNome, setPagadorOutroNome] = useState<{ pagador: PagadorUc; nome: string; total: number; motivo: string | null } | null>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const [isFetchingTitular, setIsFetchingTitular] = useState(false);
    const [isFetchingCliente, setIsFetchingCliente] = useState(false);
    // Empresa faturadora (Vigor direto ou uma parceira) — seletor visível só para adm;
    // usuário de empresa parceira sempre fatura pela própria empresa (forçado no submit).
    const [empresasParceiras, setEmpresasParceiras] = useState<{ id: string; nome: string }[]>([]);

    const [searching, setSearching] = useState(false);
    const [valorTarifaInput, setValorTarifaInput] = useState('');
    const [consumoInput, setConsumoInput] = useState('');

    const [sameNameAsClient, setSameNameAsClient] = useState(false);
    const [sameCpfAsClient, setSameCpfAsClient] = useState(false);
    const [selectedClientCpfCnpj, setSelectedClientCpfCnpj] = useState('');

    // "Sem" checkboxes for address fields
    const [semNumero, setSemNumero] = useState(false);
    const [semQuadra, setSemQuadra] = useState(false);
    const [semLote, setSemLote] = useState(false);
    const [selectedNegotiationFidelity, setSelectedNegotiationFidelity] = useState<string | undefined>(undefined);
    // Volume das demais UCs da negociação — público da política é pelo total, não só por esta UC
    const [volumeOutrasUcs, setVolumeOutrasUcs] = useState(0);
    // Percentuais manuais só aparecem quando o vendedor escolhe "Fora da política"
    const [foraDaPolitica, setForaDaPolitica] = useState(false);
    // Modelo de plano da NEGOCIAÇÃO vinculada (carimbado na criação dela), não o cargo
    // de quem abre a UC — parceiro e gerente precisam ver o mesmo formulário. UC
    // avulsa usa `plano_modalidade` da própria UC, escolhido no cartão do plano.
    // Cada modalidade tem régua própria (ver PlanoResumoCard).
    const [modalidadePlano, setModalidadePlano] = useState<ModalidadePlano>(() => modalidadePorPerfil(profile));
    // Com negociação (vinculada ou de onde o modal abriu), quem vendeu e a
    // fidelidade vêm dela; a UC avulsa guarda os próprios.
    const negociacaoVinculadaId = formData.negociacao_id ?? initialNegotiation?.id ?? null;

    // UC nova com número que já existe: em vez de só barrar, oferece a troca de
    // dono (venda do estabelecimento) para a negociação deste formulário — o
    // mesmo caminho do cadastro de negociação nova (src/lib/trocaDonoUc.ts).
    const [ucExistente, setUcExistente] = useState<UcExistente | null>(null);
    const [pedindoTroca, setPedindoTroca] = useState(false);
    const [justificativaTroca, setJustificativaTroca] = useState('');
    const [enviandoTroca, setEnviandoTroca] = useState(false);
    const avisoTrocaRef = useRef<HTMLDivElement>(null);

    const pedirTrocaDeDono = async () => {
        if (!ucExistente || enviandoTroca) return;
        const erro = validarJustificativaTroca(justificativaTroca);
        if (erro) {
            addToast(erro, 'error');
            return;
        }
        if (!negociacaoVinculadaId) {
            addToast('Escolha primeiro a negociação do novo dono no campo do cliente. A UC vai para ela.', 'warning');
            return;
        }
        setEnviandoTroca(true);
        try {
            const pct = (v: unknown) => (Number.isFinite(Number(v)) && v != null && v !== '' ? Number(v) / 100 : null);
            await solicitarTrocaDonoUc(supabase, {
                negociacaoDestinoId: Number(negociacaoVinculadaId),
                ucId: ucExistente.id,
                justificativa: justificativaTroca,
                descontoTarifa: pct(formData.desconto_tarifa),
                descontoBandeira: pct(formData.desconto_bandeira),
                carencia: formData.carencia || null,
            });
            addToast(`Troca de dono pedida. A UC ${ucExistente.numero_uc} já aparece na negociação, mas continua cobrada de ${ucExistente.nome_cliente?.trim() || 'quem é dono hoje'} até a análise ser aprovada.`, 'success');
            onSave();
            onClose();
        } catch (err: any) {
            addToast(err?.message || 'Não foi possível pedir a troca de dono.', 'error');
        } finally {
            setEnviandoTroca(false);
        }
    };
    // "Empresa parceira" é quem vendeu, não régua de desconto: fica fora de
    // `modalidadePlano` (que a política usa) e só existe na UC avulsa.
    const modalidadeVenda: ModalidadeVenda = !negociacaoVinculadaId && formData.plano_modalidade === 'empresa_parceira'
        ? 'empresa_parceira'
        : modalidadePlano;
    const empresaParceiraIdUc: string | null = (formData as any).empresa_parceira_id || null;

    // Segmento combobox
    const [segmentoSearch, setSegmentoSearch] = useState('');
    const [segmentoOpen, setSegmentoOpen] = useState(false);
    const [segmentoOutro, setSegmentoOutro] = useState('');
    const segmentoRef = useRef<HTMLDivElement>(null);

    // Autocomplete State
    const [negotiations, setNegotiations] = useState<{ id: number, cliente_nome: string, cpf_cnpj?: string }[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showQuickCreate, setShowQuickCreate] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const prevIsOpenRef = useRef(false);

    // Carrega para todos: o cartão do plano mostra o NOME da empresa da UC. Escolher
    // a empresa continua só para adm (a lista só vai para os seletores com
    // `canEscolherEmpresaFaturadora`). Sem permissão de leitura, a lista vem vazia
    // e o selo simplesmente não aparece.
    useEffect(() => {
        if (!isOpen) return;
        supabase
            .from('empresas_parceiras')
            .select('id, nome')
            .order('nome')
            .then(({ data }) => setEmpresasParceiras(data || []));
    }, [isOpen]);

    // Resolve a modalidade sempre que a UC muda de negociação vinculada — cobre os
    // quatro caminhos (edição da UC, abertura de dentro da negociação, busca e
    // criação rápida). Sem vínculo (UC avulsa) usa o cargo de quem está criando.
    useEffect(() => {
        if (!isOpen) return;
        // UC avulsa com modalidade escolhida no cadastro: vale a escolha, não o cargo
        // de quem abriu a tela (senão todo cadastro do Financeiro vira "externo").
        // 'empresa_parceira' não é modalidade da política (fica em `modalidadeVenda`,
        // abaixo): para a régua de desconto ela cai no cargo, como antes.
        const escolhida = formData.plano_modalidade as ModalidadePlano | undefined;
        if (!formData.negociacao_id && escolhida && MODALIDADES_VALIDAS.includes(escolhida)) {
            setModalidadePlano(escolhida);
            return;
        }
        let cancelado = false;
        carregarModalidadePlano(formData.negociacao_id, profile)
            .then(m => { if (!cancelado) setModalidadePlano(m); })
            .catch(err => console.error('Erro ao resolver modalidade do plano:', err));
        return () => { cancelado = true; };
    }, [isOpen, formData.negociacao_id, formData.plano_modalidade, profile?.role]);

    useEffect(() => {
        const justOpened = isOpen && !prevIsOpenRef.current;
        prevIsOpenRef.current = isOpen;

        if (!isOpen) return;
        if (!justOpened) return;

        setSelectedClientCpfCnpj('');
        setVolumeOutrasUcs(0);
        setSelectedNegotiationFidelity(undefined);

        const fetchInitialNegData = async (negId: number) => {
            try {
                const { data, error } = await supabase.from('negociacoes').select('cpf_cnpj, fidelidade').eq('id', negId).single();
                if (!error && data) {
                    if (data.cpf_cnpj) setSelectedClientCpfCnpj(data.cpf_cnpj);
                    if (data.fidelidade) setSelectedNegotiationFidelity(data.fidelidade);
                }
                fetchNegVolume(negId, unidade?.id);
            } catch (err) {
                console.error("Error fetching neg data:", err);
            }
        };

        if (unidade) {
            setFormData({
                ...unidade,
                cep: unidade.cep || '',
                logradouro: unidade.logradouro || '',
                numero: unidade.numero || '',
                complemento: unidade.complemento || '',
                quadra: unidade.quadra || '',
                lote: unidade.lote || '',
                bairro: unidade.bairro || '',
                cidade: unidade.cidade || '',
                estado: unidade.estado || '',
                // `!= null`, não truthy: 0% é desconto de verdade (ex.: bandeira 0 do
                // parceiro ou do plano sem bandeira) e virava "sem desconto definido".
                desconto_tarifa: unidade.desconto_tarifa != null ? Math.round(Number(unidade.desconto_tarifa) * 100) : undefined,
                desconto_bandeira: unidade.desconto_bandeira != null ? Math.round(Number(unidade.desconto_bandeira) * 100) : undefined,
                carencia: unidade.carencia || '',
                consumo_medio_kwh: unidade.consumo_medio_kwh,
                valor_tarifa: unidade.valor_tarifa,
                negociacao_id: unidade.negociacao_id,
                titular_fatura: unidade.titular_fatura || '',
                cpf_cnpj_titular: unidade.cpf_cnpj_titular || '',
                data_nascimento_titular: unidade.data_nascimento_titular || '',
                cpf_cnpj_cliente: unidade.cpf_cnpj_cliente || '',
                data_nascimento_cliente: unidade.data_nascimento_cliente || '',
                whatsapp_cliente: unidade.whatsapp_cliente || '',
                email_cliente: unidade.email_cliente || '',
                outro_email: unidade.outro_email || '',
                observacoes_uc: unidade.observacoes_uc || '',
                outro_numero_uc: unidade.outro_numero_uc || '',
                num_medidor: unidade.num_medidor || ''
            });
            // Sync "sem" checkboxes
            setSemNumero(unidade.numero === 'S/N' || unidade.numero === 'sn');
            setSemQuadra(unidade.quadra === 'Sem quadra');
            setSemLote(unidade.lote === 'Sem lote');
            setValorTarifaInput(unidade.valor_tarifa?.toString().replace('.', ',') || '');
            setConsumoInput(unidade.consumo_medio_kwh?.toString().replace('.', ',') || '');
            setForaDaPolitica(!!resolverPlanoAtivo(
                unidade.desconto_tarifa ? Math.round(unidade.desconto_tarifa * 100) : undefined,
                unidade.desconto_bandeira ? Math.round(unidade.desconto_bandeira * 100) : undefined
            )?.foraDaPolitica);

            // Segmento: se o valor salvo não está na lista de opções, é um "Outro"
            const isKnownSegmento = SEGMENTO_OPTIONS.includes(unidade.segmento || '');
            if (unidade.segmento && !isKnownSegmento) {
                setFormData(prev => ({ ...prev, segmento: 'Outro' }));
                setSegmentoOutro(unidade.segmento || '');
            } else {
                setSegmentoOutro('');
            }

            if (unidade.negociacao_id) {
                fetchInitialNegData(unidade.negociacao_id);
            }
        } else {
            const defaults: Partial<UnidadeConsumidora> = {
                numero_uc: '',
                nome_cliente: initialNegotiation?.cliente_nome || '',
                negociacao_id: initialNegotiation?.id,
                endereco: '',
                situacao: null,
                status_financeiro: 'Não se aplica',
                cep: '',
                logradouro: '',
                numero: '',
                complemento: '',
                quadra: '',
                lote: '',
                bairro: '',
                cidade: '',
                estado: '',
                // Plano oficial da política comercial (22% + 50% bandeira) — 20/50 não existe na política nova
                desconto_tarifa: 22,
                desconto_bandeira: 50,
                // Padrão comercial: sem carência — carência só via Alterar Plano com aprovação de gerente/admin
                carencia: 'sem carência',
                consumo_medio_kwh: undefined,
                valor_tarifa: undefined,
                titular_fatura: '',
                cpf_cnpj_titular: '',
                data_nascimento_titular: '',
                cpf_cnpj_cliente: '',
                data_nascimento_cliente: '',
                whatsapp_cliente: '',
                email_cliente: '',
                outro_email: '',
                observacoes_uc: '',
                outro_numero_uc: '',
                num_medidor: ''
            };
            const merged = prefill ? { ...defaults, ...prefill } : defaults;
            setFormData(merged);
            setForaDaPolitica(!!resolverPlanoAtivo(merged.desconto_tarifa, merged.desconto_bandeira)?.foraDaPolitica);
            setSemNumero(merged.numero === 'S/N' || merged.numero === 'sn');
            setSemQuadra(merged.quadra === 'Sem quadra');
            setSemLote(merged.lote === 'Sem lote');
            setValorTarifaInput(merged.valor_tarifa?.toString().replace('.', ',') || '');
            setConsumoInput(merged.consumo_medio_kwh?.toString().replace('.', ',') || '');
            setSameNameAsClient(false);
            setSameCpfAsClient(false);

            // Segmento vindo do prefill: se não está na lista de opções, é um "Outro"
            const isKnownSegmento = SEGMENTO_OPTIONS.includes(merged.segmento || '');
            if (merged.segmento && !isKnownSegmento) {
                setFormData(prev => ({ ...prev, segmento: 'Outro' }));
                setSegmentoOutro(merged.segmento || '');
            } else {
                setSegmentoOutro('');
            }

            if (merged.negociacao_id) {
                fetchInitialNegData(merged.negociacao_id);
            } else if (initialNegotiation?.id) {
                fetchInitialNegData(initialNegotiation.id);
            }
        }
    }, [unidade, isOpen, initialNegotiation, prefill]);

    // Click outside to close suggestions / segmento dropdown
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
            if (segmentoRef.current && !segmentoRef.current.contains(event.target as Node)) {
                setSegmentoOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Soma o consumo das outras UCs da negociação para classificar o público da política
    const fetchNegVolume = async (negId: number, ucAtualId?: number) => {
        try {
            const { data } = await supabase
                .from('unidades_consumidoras')
                .select('id, consumo_medio_kwh')
                .eq('negociacao_id', negId);
            const soma = (data || [])
                .filter(u => !ucAtualId || u.id !== ucAtualId)
                .reduce((s, u) => s + (Number(u.consumo_medio_kwh) || 0), 0);
            setVolumeOutrasUcs(soma);
        } catch {
            setVolumeOutrasUcs(0);
        }
    };

    const fetchNegotiations = async (search: string) => {
        if (search.length < 2) {
            setNegotiations([]);
            return;
        }

        const { data } = await supabase
            .from('negociacoes')
            .select('id, cliente_nome, cpf_cnpj, fidelidade')
            .ilike('cliente_nome', `%${search}%`)
            .limit(5);

        if (data) setNegotiations(data);
    };



    const formatCpfCnpj = (val: string) => {
        const cleanVal = val.replace(/\D/g, '');
        if (cleanVal.length <= 11) {
            // CPF mask: 000.000.000-00
            let formatted = cleanVal;
            if (formatted.length > 3) formatted = `${formatted.slice(0, 3)}.${formatted.slice(3)}`;
            if (formatted.length > 7) formatted = `${formatted.slice(0, 7)}.${formatted.slice(7)}`;
            if (formatted.length > 11) formatted = `${formatted.slice(0, 11)}-${formatted.slice(11)}`;
            return formatted;
        } else {
            // CNPJ mask: 00.000.000/0000-00
            let formatted = cleanVal.slice(0, 14);
            if (formatted.length > 2) formatted = `${formatted.slice(0, 2)}.${formatted.slice(2)}`;
            if (formatted.length > 6) formatted = `${formatted.slice(0, 6)}.${formatted.slice(6)}`;
            if (formatted.length > 10) formatted = `${formatted.slice(0, 10)}/${formatted.slice(10)}`;
            if (formatted.length > 15) formatted = `${formatted.slice(0, 15)}-${formatted.slice(15)}`;
            return formatted;
        }
    };

    const handleAutofillCpfCnpj = async (type: 'titular' | 'cliente') => {
        const cpfCnpj = type === 'titular' ? formData.cpf_cnpj_titular : formData.cpf_cnpj_cliente;
        const cleanCpfCnpj = cpfCnpj?.replace(/\D/g, '') || '';

        if (cleanCpfCnpj.length !== 11 && cleanCpfCnpj.length !== 14) return;

        const setLocalLoading = type === 'titular' ? setIsFetchingTitular : setIsFetchingCliente;
        setLocalLoading(true);

        try {
            const { data, error } = await supabase.functions.invoke('consultar-cpf-cnpj', {
                body: { document: cleanCpfCnpj, type: cleanCpfCnpj.length === 11 ? 'cpf' : 'cnpj' }
            });

            if (error) throw error;
            if (data?.error) throw new Error(data.error);

            if (data && data.nome) {
                if (type === 'titular') {
                    setFormData(prev => ({
                        ...prev,
                        titular_fatura: data.nome,
                        ...(data.nascimento ? { data_nascimento_titular: data.nascimento } : {})
                    }));
                } else {
                    setFormData(prev => ({
                        ...prev,
                        nome_cliente: data.nome,
                        ...(data.nascimento ? { data_nascimento_cliente: data.nascimento } : {})
                    }));
                }
                addToast('Dados preenchidos com sucesso!', 'success');
            } else {
                addToast('Nenhum dado encontrado para este documento.', 'warning');
            }
        } catch (err: any) {
            console.error('Erro ao buscar CPF/CNPJ:', err);
            addToast('Erro ao consultar dados da Receita Federal/CPF.', 'error');
        } finally {
            setLocalLoading(false);
        }
    };

    const handleSearchCEP = async () => {
        const cep = formData.cep?.replace(/\D/g, '');
        if (cep?.length !== 8) return;

        setSearching(true);
        try {
            const response = await fetch(`https://brasilapi.com.br/api/cep/v2/${cep}`);
            if (!response.ok) throw new Error('CEP não encontrado');

            const data = await response.json();
            setFormData(prev => ({
                ...prev,
                logradouro: data.street || '',
                bairro: data.neighborhood || '',
                cidade: data.city || '',
                estado: data.state || ''
            }));
        } catch (error) {
            console.error('Error fetching CEP:', error);
        } finally {
            setSearching(false);
        }
    };

    const handleClientNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        // Aberto de dentro de uma negociação, o vínculo é fixo: corrigir o nome não pode
        // desvincular a UC silenciosamente (UC sem negociacao_id não aparece na negociação).
        setFormData({ ...formData, nome_cliente: val, negociacao_id: initialNegotiation?.id });
        fetchNegotiations(val);
        setShowSuggestions(true);
    };

    const selectNegotiation = (neg: { id: number, cliente_nome: string, cpf_cnpj?: string, fidelidade?: string }) => {
        setFormData(prev => ({
            ...prev,
            nome_cliente: neg.cliente_nome,
            negociacao_id: neg.id,
            cpf_cnpj_cliente: neg.cpf_cnpj ? neg.cpf_cnpj.replace(/\D/g, '') : prev.cpf_cnpj_cliente,
            cpf_cnpj_titular: (sameCpfAsClient && neg.cpf_cnpj) ? neg.cpf_cnpj.replace(/\D/g, '') : prev.cpf_cnpj_titular,
            titular_fatura: (sameNameAsClient && neg.cliente_nome) ? neg.cliente_nome : prev.titular_fatura
        }));
        setSelectedClientCpfCnpj(neg.cpf_cnpj || '');
        setSelectedNegotiationFidelity(neg.fidelidade);
        fetchNegVolume(neg.id, unidade?.id);
        setShowSuggestions(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // "Empresa parceira" sem a empresa gravaria uma venda de parceira sem dizer
        // de qual — e a fatura seguiria pela Vigor, sem o split da parceira.
        if (!negociacaoVinculadaId && modalidadeVenda === 'empresa_parceira' && !empresaParceiraIdUc) {
            addToast('Escolha qual empresa parceira vendeu esta UC, no cartão do plano.', 'warning');
            return;
        }

        // UC nova sempre entra no volume da negociação; em edição, só conta se o
        // número de fato mudou. `Number()` dos dois lados porque o banco devolve
        // `numeric` como string e a comparação crua acusaria mudança sempre.
        const consumoAnterior = unidade ? Number(unidade.consumo_medio_kwh) || 0 : null;
        const consumoNovo = Number(formData.consumo_medio_kwh) || 0;
        const consumoMudou = consumoAnterior === null || consumoAnterior !== consumoNovo;

        // A régua de aprovação de desconto só entra quando a CONDIÇÃO COMERCIAL
        // mudou — mesma razão da CAMADA 1 do recálculo, logo abaixo: até aqui
        // qualquer salvamento desta tela reexecutava a régua, então corrigir um
        // CEP ou o consumo bastava para abrir um pedido de aprovação. Enquanto a
        // carência ficava de fora da checagem (solicitação 5916) isso passava
        // despercebido; ao entrar, viraria enxurrada — 41,5% das UCs têm carência
        // de verdade (amostra de 1.000, quase toda "até 90 dias"), e o gerente
        // receberia pedido em 4 de cada 10 edições em que ninguém tocou no desconto.
        //
        // UC nova sempre passa pela régua: não há "antes" para comparar, e é aí
        // que a condição nasce.
        // Regra em src/lib/condicaoComercialMudou.ts, com teste próprio. O banco
        // guarda fração (0.15) e a tela trabalha em percentual (15) — comparar sem
        // converter acusaria mudança em todo salvamento e anularia a guarda.
        // A carência é avaliada SÓ quando muda nesta edição. Carência que já
        // estava na UC não pode pedir permissão de novo: ela passou pela régua
        // quando foi definida, e cobrar de novo travaria um ajuste que não
        // precisa de ninguém — 41,5% das UCs têm carência (amostra de 1.000).
        const carenciaMudou = !unidade
            || String(unidade.carencia ?? '').trim() !== String(formData.carencia ?? '').trim();

        const condicaoMudou = condicaoComercialMudou(
            unidade
                ? {
                    descontoTarifa: fracaoParaPercentual(unidade.desconto_tarifa),
                    descontoBandeira: fracaoParaPercentual(unidade.desconto_bandeira),
                    carencia: unidade.carencia,
                }
                : null,
            {
                descontoTarifa: formData.desconto_tarifa,
                descontoBandeira: formData.desconto_bandeira,
                carencia: formData.carencia,
            },
        );

        // CAMADA 2 — mostra o efeito ANTES de gravar. O aviso de "público
        // reclassificado" chegava depois do fato consumado, sem chance de desistir.
        if (consumoMudou && formData.negociacao_id && !impactoConfirmado) {
            setLoading(true);
            try {
                const [sim, statusComissao] = await Promise.all([
                    recalcularCondicaoComercial(Number(formData.negociacao_id), { simular: true }),
                    buscarStatusComissao(Number(formData.negociacao_id)),
                ]);
                const impacto = avaliarImpactoEdicao({
                    consumoMudou: true,
                    condicaoMudaria: !!sim?.mudou,
                    statusComissao,
                    publicoAnterior: sim?.anterior.publico,
                    publicoNovo: sim?.atual.publico,
                    multiplicadorAnterior: sim?.anterior.multiplicadorFinal,
                    multiplicadorNovo: sim?.atual.final,
                });
                setLoading(false);
                // Só interrompe quando há de fato algo a confirmar. Pedir confirmação
                // de um salvamento inócuo treinaria o operador a clicar sem ler.
                if (impacto.exigencia !== 'livre') {
                    setImpactoEdicao(impacto);
                    return;
                }
            } catch (err) {
                console.error('Falha ao simular o impacto da edição de consumo:', err);
                setLoading(false);
            }
        }

        setLoading(true);

        // Mesma UC já cadastrada — no "Número da UC" ou no "Outro Nº UC" de outro
        // cadastro, com ou sem máscara. Só os números que esta UC ainda não tinha:
        // inverter os dois ou tirar a máscara não é duplicar (mesma regra da trigger
        // trg_bloqueia_uc_duplicada, que barra de qualquer jeito na gravação).
        const chavesAntigas = unidade ? ucCodigoKeys(unidade.numero_uc, unidade.outro_numero_uc) : [];
        const chavesNovas = ucCodigoKeys(formData.numero_uc, formData.outro_numero_uc).filter(k => !chavesAntigas.includes(k));
        if (chavesNovas.length > 0) {
            try {
                const existente = await buscarUcComMesmoNumero(supabase, chavesNovas[0], chavesNovas[1], unidade?.id);
                if (existente) {
                    if (!unidade) {
                        // Cadastro novo: abre o aviso com a saída da troca de dono.
                        setUcExistente(existente);
                        setPedindoTroca(false);
                        setJustificativaTroca('');
                        addToast(`${descreverUcExistente(existente)} Se o estabelecimento foi vendido, peça a troca de dono no aviso do formulário.`, 'error');
                        avisoTrocaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    } else {
                        addToast(descreverUcExistente(existente), 'error');
                    }
                    setLoading(false);
                    return;
                }
            } catch (err) {
                console.error('Falha ao checar UC duplicada:', err);
            }
        }

        // Update legacy address field for compatibility
        const fullAddress = `${formData.logradouro}, ${formData.numero} - ${formData.bairro}, ${formData.cidade}/${formData.estado}`;

        // Sanitize data (convert empty strings to null for Enums/Optionals)
        const segmentoFinal = formData.segmento === 'Outro'
            ? (segmentoOutro.trim() || null)
            : (formData.segmento || null);

        // Fields managed exclusively by UCManagementModal — never overwrite them here.
        // numero_uc_norm/outro_numero_uc_norm são colunas geradas (GENERATED ALWAYS AS ... STORED);
        // o Postgres rejeita qualquer UPDATE que as inclua, mesmo com o mesmo valor.
        // pagador_id é da seção "Pagador do boleto" (PagadorUcSecao), na mesma aba: o
        // valor carregado ao abrir a tela desfazia a troca de pagador feita ali antes
        // de clicar em "Salvar Unidade". motivo_alteracao vai só quando confirmado.
        const { documentos_json, url_termo, url_simulacao, emitir_em_vigor_gestao, conta_sicoob, receber_cobranca_email, boleto_unico, numero_uc_norm, outro_numero_uc_norm, pagador_id, motivo_alteracao, ...restFormData } = formData as any;

        // CAMADA 3 — rastro. Sem origem e autor, "por que este número está aqui?"
        // não tem resposta: pode ser estimativa do vendedor, resultado da análise
        // cadastral ou digitação de oito meses atrás.
        const rastroConsumo = consumoMudou
            ? {
                consumo_medio_origem: 'manual',
                consumo_medio_definido_por: profile?.full_name || user?.email || null,
                consumo_medio_definido_em: new Date().toISOString(),
                consumo_medio_justificativa: justificativaEdicao.trim() || null,
            }
            : {};

        const dataToSave = {
            ...restFormData,
            ...rastroConsumo,
            // Rede de segurança: sem vínculo explícito, herda a negociação de onde o modal foi aberto.
            negociacao_id: formData.negociacao_id ?? initialNegotiation?.id ?? null,
            segmento: segmentoFinal,
            endereco: fullAddress,
            tipo_ligacao: formData.tipo_ligacao || null,
            grupo_tarifario: formData.grupo_tarifario || null,
            concessionaria: formData.concessionaria || null,
            carencia: formData.carencia || null,
            valor_tarifa: formData.valor_tarifa || null,
            // 0% é valor, não ausência. Gravado como NULL, o cálculo do boleto
            // (FaturaCalculationModal) trocava pelo padrão — 50% de bandeira — e
            // ainda escrevia esse padrão de volta na UC.
            desconto_tarifa: Number.isFinite(Number(formData.desconto_tarifa)) && formData.desconto_tarifa != null ? Number(formData.desconto_tarifa) / 100 : null,
            desconto_bandeira: Number.isFinite(Number(formData.desconto_bandeira)) && formData.desconto_bandeira != null ? Number(formData.desconto_bandeira) / 100 : null,
            // Só a UC avulsa guarda quem vendeu e a fidelidade; com negociação, valem
            // os dela (NULL aqui evita duas fontes de verdade se a UC for vinculada depois).
            plano_modalidade: negociacaoVinculadaId ? null : modalidadeVenda,
            fidelidade: negociacaoVinculadaId ? null : (formData.fidelidade || null),
            situacao: formData.situacao ? formData.situacao.trim() : null,
            status_financeiro: formData.status_financeiro && formData.status_financeiro.trim() !== 'Não se aplica' ? formData.status_financeiro.trim() : 'Não se aplica',
            titular_fatura: formData.titular_fatura || null,
            cpf_cnpj_titular: formData.cpf_cnpj_titular || null,
            data_nascimento_titular: formData.data_nascimento_titular || null,
            cpf_cnpj_cliente: formData.cpf_cnpj_cliente || null,
            data_nascimento_cliente: formData.data_nascimento_cliente || null,
            whatsapp_cliente: formData.whatsapp_cliente || null,
            email_cliente: formData.email_cliente || null,
            outro_email: formData.outro_email || null,
            observacoes_uc: formData.observacoes_uc || null,
            outro_numero_uc: formData.outro_numero_uc || null,
            num_medidor: formData.num_medidor || null,
            quadra: formData.quadra || null,
            lote: formData.lote || null,
            percentual_vigor: formData.percentual_vigor != null && Number.isFinite(Number(formData.percentual_vigor)) && String(formData.percentual_vigor) !== ''
                ? Number(formData.percentual_vigor)
                : null,
            // Usuário de empresa parceira só cadastra UC para a própria empresa (RLS já
            // reforça isso); demais roles preservam o que já estava em formData (undefined
            // em criação nova, ou o valor herdado da UC em edição).
            empresa_parceira_id: (profile?.role === 'empresa_parceira_admin' || profile?.role === 'empresa_parceira_operador')
                ? profile?.empresa_parceira_id
                : (formData as any).empresa_parceira_id,
        };

        // Sem permissão, os dados cadastrais ficam fora do UPDATE e o resto da UC
        // (consumo, plano, situação, observações) salva normal.
        const payloadEdicao = unidade ? (cadastroTravado ? semCamposCadastrais(dataToSave) : dataToSave) : null;
        const motivo = motivoConfirmadoRef.current;
        if (unidade && payloadEdicao && ucComCadastroTravado(unidade) && !motivo) {
            const mudancas = camposAlterados(unidade as any, payloadEdicao);
            if (mudancas.length > 0) {
                setPedidoMotivo(mudancas);
                setLoading(false);
                return;
            }
        }

        try {
            if (unidade && payloadEdicao) {
                let { error } = await supabase
                    .from('unidades_consumidoras')
                    .update(comMotivo(payloadEdicao, motivo))
                    .eq('id', unidade.id);
                // Banco ainda sem a migration 20261029180000: grava sem o motivo.
                if (error && motivo && erroColunaMotivoAusente(error)) {
                    ({ error } = await supabase
                        .from('unidades_consumidoras')
                        .update(payloadEdicao)
                        .eq('id', unidade.id));
                    if (!error) addToast('Unidade salva, mas o motivo não foi registrado: falta atualizar o banco. Avise a Tecnologia.', 'warning', 10000);
                }

                if (error) throw error;

                if (user?.id) {
                    await logAction(user.id, 'Atualizou Unidade Consumidora', {
                        uc_id: unidade.id,
                        uc_number: dataToSave.numero_uc,
                        cliente: dataToSave.nome_cliente
                    });
                    logUsageEvent({ userId: user.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'atualizar_uc', origem: 'acao' });
                }
            } else {
                // Create
                const { data, error } = await supabase
                    .from('unidades_consumidoras')
                    .insert([dataToSave])
                    .select();

                if (error) throw error;

                if (user?.id && data && data[0]) {
                    await logAction(user.id, 'Criou Unidade Consumidora', {
                        uc_id: data[0].id,
                        uc_number: dataToSave.numero_uc,
                        cliente: dataToSave.nome_cliente
                    });
                    logUsageEvent({ userId: user.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'criar_uc', origem: 'acao' });
                }
            }

            if (dataToSave.negociacao_id) {
                const action = unidade ? 'editou a UC' : 'adicionou a UC';
                await logNegotiationAction(dataToSave.negociacao_id, user?.id, action, { uc: dataToSave.numero_uc });
                logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'gerenciar_uc_negociacao', origem: 'acao' });

                // Só EXIGE permissão o que MUDOU nesta edição (decisão do dono do
                // produto, 21/09/2026). Duas consequências deliberadas:
                //
                // 1. `planoCodigo` NÃO é enviado. Mandá-lo tornaria a régua mais
                //    severa sobre o DESCONTO, e desconto é dinheiro: uma condição que
                //    o vendedor fecha sozinho hoje passaria a travar esperando
                //    gerente. A avaliação do desconto fica exatamente como sempre foi
                //    (ramo de compatibilidade: aprova até 20% tarifa / 50% bandeira).
                //
                // 2. `carencia` só vai quando mudou. Carência preexistente já passou
                //    pela régua quando foi definida; reavaliá-la faria um ajuste
                //    qualquer de cadastro pedir aprovação por algo que ninguém tocou.
                //
                // O que a 5916 fecha, então, é só isto: carência ACRESCENTADA pelo
                // cadastro da UC deixa de escapar da aprovação. O desconto segue
                // intocado.
                if (
                    condicaoMudou
                    && dataToSave.desconto_tarifa !== null
                    && dataToSave.desconto_bandeira !== null
                ) {
                    await checkAndRequestDiscountApproval(
                        dataToSave.negociacao_id,
                        user?.id,
                        (dataToSave.desconto_tarifa || 0) * 100,
                        (dataToSave.desconto_bandeira || 0) * 100,
                        selectedNegotiationFidelity,
                        { carencia: carenciaMudou ? dataToSave.carencia : null }
                    );
                }

                // CAMADA 1 — só reclassifica quando o CONSUMO mudou.
                //
                // Antes, qualquer salvamento desta tela chamava o recálculo: corrigir um
                // CEP reescrevia público e multiplicador_final da negociação, que é a
                // base da comissão (`comissaoExternaV2` o trata como snapshot). Um campo
                // de cadastro mexia em dinheiro sem nenhuma relação de causa.
                if (consumoMudou) {
                    try {
                        const recalc = await recalcularCondicaoComercial(Number(dataToSave.negociacao_id));
                        if (recalc?.mudou) {
                            addToast(
                                `Volume da negociação mudou: público reclassificado para ${recalc.atual.publico} e multiplicador recalculado para ${recalc.atual.final.toFixed(2).replace('.', ',')}x.`,
                                'info'
                            );
                        }
                    } catch (recalcErr) {
                        console.error('Erro ao reclassificar condição comercial:', recalcErr);
                    }
                }

                // Create feed note from UC observations when changed
                const obsChanged = dataToSave.observacoes_uc && dataToSave.observacoes_uc.trim() &&
                    (!unidade || (unidade.observacoes_uc || '') !== dataToSave.observacoes_uc);
                if (obsChanged) {
                    await supabase.from('negociacao_notas').insert({
                        negociacao_id: dataToSave.negociacao_id,
                        user_id: user?.id,
                        conteudo: `[UC ${dataToSave.numero_uc}] ${dataToSave.observacoes_uc}`,
                        tipo: 'nota'
                    });
                }
            }

            // #6565: o boleto sai pelo nome do pagador. Trocou o cliente e o pagador
            // com o mesmo CPF/CNPJ ficou com o nome antigo: pergunta antes de fechar.
            if (unidade && payloadEdicao && !cadastroTravado
                && (payloadEdicao.nome_cliente !== unidade.nome_cliente || payloadEdicao.cpf_cnpj_cliente !== unidade.cpf_cnpj_cliente)) {
                const pagador = await buscarPagadorDaUc(unidade.id);
                if (pagadorComOutroNome(pagador, payloadEdicao.nome_cliente, payloadEdicao.cpf_cnpj_cliente)) {
                    if (podeEditarCadastro(profile)) {
                        setPagadorOutroNome({ pagador: pagador!, nome: String(payloadEdicao.nome_cliente).trim(), total: await contarUcsDoPagador(pagador!.id), motivo });
                        return;
                    }
                    addToast(`O pagador do boleto continua como ${pagador!.nome}. Peça ao Financeiro para atualizar o pagador.`, 'warning', 10000);
                }
            }

            onSave();
            onClose();
        } catch (error: any) {
            console.error('Error saving unidade:', error);
            const duplicada = mensagemUcDuplicada(error);
            if (duplicada) {
                addToast(duplicada, 'error');
            } else if (error.code === '23505') {
                // Diz onde a UC já está para o usuário conseguir resolver o vínculo (dado legado
                // pode existir sem negociação ou preso a uma negociação antiga).
                const { data: existente } = await supabase
                    .from('unidades_consumidoras')
                    .select('negociacao_id, negociacoes(cliente_nome)')
                    .eq('numero_uc', dataToSave.numero_uc)
                    .maybeSingle();
                const vinculo = existente?.negociacao_id
                    ? `vinculada à negociação de "${(existente as any).negociacoes?.cliente_nome || `ID ${existente.negociacao_id}`}"`
                    : 'cadastrada sem vínculo com negociação (edite-a pela tela de Unidades Consumidoras para vinculá-la)';
                addToast(`Erro: a UC ${dataToSave.numero_uc} já existe no sistema, ${vinculo}.`, 'error');
            } else {
                addToast('Erro ao salvar unidade consumidora: ' + (error.message || 'Erro desconhecido'), 'error');
            }
        } finally {
            // O motivo vale só para esta gravação: a próxima pergunta de novo.
            motivoConfirmadoRef.current = null;
            setLoading(false);
        }
    };

    return (
        <form ref={formRef} onSubmit={handleSubmit} className="p-6 space-y-6">

            {/* Confirmação do efeito comercial da edição de consumo (Camadas 2 e 3).
                Aparece ANTES de gravar, com o efeito já calculado pela simulação. */}
            {impactoEdicao && createPortal(
                <div className="fixed inset-0 z-[3000] bg-black/50 flex items-center justify-center p-4" onClick={() => setImpactoEdicao(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
                        <h3 className={`text-base font-bold mb-2 ${impactoEdicao.grave ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                            {impactoEdicao.grave ? 'Esta alteração mexe em comissão' : 'Confirmar alteração de consumo'}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{impactoEdicao.aviso}</p>

                        {exigeJustificativa(impactoEdicao.exigencia) && (
                            <div className="mb-4">
                                <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                                    Motivo da correção
                                </label>
                                <textarea
                                    value={justificativaEdicao}
                                    onChange={e => setJustificativaEdicao(e.target.value)}
                                    rows={3}
                                    autoFocus
                                    placeholder="Ex: cadastro tinha 137 kWh num B3; corrigido pela média das faturas."
                                    className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm dark:bg-slate-800 dark:text-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                                />
                            </div>
                        )}

                        <div className="flex gap-2 justify-end">
                            <button
                                type="button"
                                onClick={() => { setImpactoEdicao(null); setJustificativaEdicao(''); }}
                                className="px-4 py-2 rounded-lg border border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-600 dark:text-gray-300"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                disabled={exigeJustificativa(impactoEdicao.exigencia) && !justificativaValida(justificativaEdicao)}
                                onClick={() => {
                                    // `impactoConfirmado` faz o próximo submit passar direto pela
                                    // simulação — sem ele a tela pediria confirmação em laço.
                                    setImpactoConfirmado(true);
                                    setImpactoEdicao(null);
                                    requestAnimationFrame(() => formRef.current?.requestSubmit());
                                }}
                                className={`px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed ${impactoEdicao.grave ? 'bg-red-600' : 'bg-primary'}`}
                            >
                                Confirmar e salvar
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {pagadorOutroNome ? (
                <PagadorOutroNomeModal
                    pagador={pagadorOutroNome.pagador}
                    nomeCliente={pagadorOutroNome.nome}
                    totalUcs={pagadorOutroNome.total}
                    usuarioId={user?.id}
                    motivo={pagadorOutroNome.motivo}
                    onFim={atualizou => {
                        setPagadorOutroNome(null);
                        addToast(atualizou ? 'Unidade salva e pagador do boleto atualizado.' : 'Unidade salva. O pagador do boleto ficou como estava.', atualizou ? 'success' : 'info');
                        onSave();
                        onClose();
                    }}
                />
            ) : null}

            {pedidoMotivo ? (
                <MotivoAlteracaoModal
                    campos={pedidoMotivo}
                    onCancelar={() => setPedidoMotivo(null)}
                    onConfirmar={m => {
                        motivoConfirmadoRef.current = m;
                        setPedidoMotivo(null);
                        requestAnimationFrame(() => formRef.current?.requestSubmit());
                    }}
                />
            ) : null}

            {cadastroTravado ? (
                <div className="flex items-start gap-3 rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-900 dark:text-amber-200">
                    <Lock size={18} className="shrink-0 mt-0.5" />
                    <p>
                        <strong className="block">Dados cadastrais travados</strong>
                        {MENSAGEM_CADASTRO_TRAVADO} Consumo, plano, situação e observações continuam liberados.
                    </p>
                </div>
            ) : null}

            {/* --- ÁREA DA DISTRIBUIDORA --- */}
            <div className="bg-orange-50/50 dark:bg-orange-500/5 rounded-2xl p-5 border border-orange-100/50 dark:border-orange-500/10 space-y-4">
                <h4 className="uppercase font-bold text-orange-800 dark:text-orange-500 mb-2 flex items-center">
                    Dados da Fatura (Distribuidora)
                </h4>

                {/* Titular Fields */}
                <fieldset disabled={cadastroTravado} className="min-w-0 disabled:opacity-60">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <div className="flex items-center gap-2 mb-1 justify-between">
                            <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase">
                                Nome do Titular
                            </label>
                            <label className="flex items-center gap-1 text-[10px] text-primary cursor-pointer hover:bg-primary/5 dark:hover:bg-primary/10 px-1 py-0.5 rounded transition-colors break-keep whitespace-nowrap">
                                <input
                                    type="checkbox"
                                    checked={sameNameAsClient}
                                    onChange={(e) => {
                                        setSameNameAsClient(e.target.checked);
                                        if (e.target.checked && formData.nome_cliente) {
                                            setFormData(prev => ({ ...prev, titular_fatura: formData.nome_cliente }));
                                        }
                                    }}
                                    className="rounded border-gray-300 dark:border-slate-700 text-primary focus:ring-primary w-3 h-3 dark:bg-slate-800"
                                />
                                O mesmo da negociação
                            </label>
                        </div>
                        <input
                            type="text"
                            value={formData.titular_fatura || ''}
                            onChange={(e) => {
                                setFormData({ ...formData, titular_fatura: e.target.value });
                                if (sameNameAsClient) setSameNameAsClient(false);
                            }}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white"
                            placeholder="Nome Completo do Titular"
                        />
                    </div>
                    <div>
                        <div className="flex items-center gap-2 mb-1 justify-between">
                            <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase tracking-tighter sm:tracking-normal">
                                CPF/CNPJ do Titular
                            </label>
                            <label className="flex items-center gap-1 text-[10px] text-primary cursor-pointer hover:bg-primary/5 dark:hover:bg-primary/10 px-1 py-0.5 rounded transition-colors break-keep whitespace-nowrap">
                                <input
                                    type="checkbox"
                                    checked={sameCpfAsClient}
                                    onChange={(e) => {
                                        setSameCpfAsClient(e.target.checked);
                                        if (e.target.checked && selectedClientCpfCnpj) {
                                            const formattedCpfCnpj = selectedClientCpfCnpj.replace(/\D/g, '');
                                            setFormData(prev => ({ ...prev, cpf_cnpj_titular: formattedCpfCnpj }));
                                        }
                                    }}
                                    className="rounded border-gray-300 dark:border-slate-700 text-primary focus:ring-primary w-3 h-3 dark:bg-slate-800"
                                />
                                O mesmo da negociação
                            </label>
                        </div>
                        <div className="relative">
                            <input
                                type="text"
                                value={formatCpfCnpj(formData.cpf_cnpj_titular || '')}
                                onChange={(e) => {
                                    const cleanValue = e.target.value.replace(/\D/g, '');
                                    if (cleanValue.length <= 14) {
                                        setFormData({ ...formData, cpf_cnpj_titular: cleanValue });
                                        if (sameCpfAsClient) setSameCpfAsClient(false);
                                    }
                                }}
                                className={`w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 ${canConsultCpfCnpj ? 'pr-10' : ''} text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white`}
                                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                            />
                            {canConsultCpfCnpj && (
                                <button
                                    type="button"
                                    onClick={() => handleAutofillCpfCnpj('titular')}
                                    disabled={!formData.cpf_cnpj_titular || (formData.cpf_cnpj_titular.replace(/\D/g, '').length !== 11 && formData.cpf_cnpj_titular.replace(/\D/g, '').length !== 14) || isFetchingTitular}
                                    className="absolute right-2 top-2 p-1 text-gray-400 hover:text-primary disabled:opacity-50 transition-colors"
                                    title="Buscar dados do CPF/CNPJ"
                                >
                                    {isFetchingTitular ? (
                                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <Search size={20} className={(!formData.cpf_cnpj_titular || (formData.cpf_cnpj_titular.replace(/\D/g, '').length !== 11 && formData.cpf_cnpj_titular.replace(/\D/g, '').length !== 14)) ? 'opacity-30' : 'text-primary'} />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Data de Nascimento do Titular
                        </label>
                        <input
                            type="date"
                            value={formData.data_nascimento_titular || ''}
                            onChange={(e) => setFormData({ ...formData, data_nascimento_titular: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-[9px] text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none bg-white dark:bg-slate-800 dark:text-white"
                        />
                    </div>
                </div>
                </fieldset>

                <div className="grid grid-cols-5 gap-3">
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Número da UC <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            disabled={cadastroTravado}
                            value={formData.numero_uc}
                            onChange={e => { setFormData({ ...formData, numero_uc: e.target.value }); setUcExistente(null); }}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                            placeholder="Ex: 12345678"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Outro Nº UC
                        </label>
                        <input
                            type="text"
                            disabled={cadastroTravado}
                            value={formData.outro_numero_uc || ''}
                            onChange={e => { setFormData({ ...formData, outro_numero_uc: e.target.value }); setUcExistente(null); }}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                            placeholder="Ex: 12345678"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Nº do Medidor
                        </label>
                        <input
                            type="text"
                            value={formData.num_medidor || ''}
                            onChange={e => setFormData({ ...formData, num_medidor: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white"
                            placeholder="Ex: 00123456"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Concessionária
                        </label>
                        <select
                            value={formData.concessionaria || ''}
                            onChange={e => setFormData({ ...formData, concessionaria: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none bg-white dark:bg-slate-800 dark:text-white"
                        >
                            <option value="">Selecione...</option>
                            <option value="Equatorial Goiás">Equatorial Goiás</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Consumo Médio (kWh)
                        </label>
                        <input
                            type="text"
                            value={consumoInput}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val.includes('.')) return;
                                if (/^[0-9]*[,]?[0-9]*$/.test(val)) {
                                    setConsumoInput(val);
                                }
                            }}
                            onBlur={() => {
                                if (consumoInput) {
                                    const numVal = parseFloat(consumoInput.replace(',', '.'));
                                    setFormData(prev => ({ ...prev, consumo_medio_kwh: isNaN(numVal) ? undefined : numVal }));
                                } else {
                                    setFormData(prev => ({ ...prev, consumo_medio_kwh: undefined }));
                                }
                            }}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white"
                            placeholder="Ex: 350,50"
                        />
                        {/* O número real, ao lado do campo. Antes o operador digitava às
                            cegas mesmo existindo a média das faturas calculada todo dia. */}
                        {Number(unidade?.consumo_observado_kwh) > 0 && (
                            <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[11px]">
                                <span className="text-gray-500 dark:text-gray-400">
                                    Observado nas faturas:{' '}
                                    <strong className="text-gray-700 dark:text-gray-200 tabular-nums">
                                        {Number(unidade!.consumo_observado_kwh).toLocaleString('pt-BR')} kWh
                                    </strong>
                                    <span className="text-gray-400 dark:text-gray-500">
                                        {' '}· {unidade!.consumo_observado_meses} faturas
                                    </span>
                                </span>
                                {Number(unidade?.consumo_observado_dispersao) > 0.6 && (
                                    <span className="font-semibold text-red-600 dark:text-red-400" title="Os meses variam demais entre si — confira o histórico antes de adotar.">
                                        instável
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={() => {
                                        const v = Number(unidade!.consumo_observado_kwh);
                                        setConsumoInput(String(v).replace('.', ','));
                                        setFormData(prev => ({ ...prev, consumo_medio_kwh: v }));
                                    }}
                                    className="px-2 py-0.5 rounded-md border border-primary/40 text-primary font-medium hover:bg-primary/10"
                                >
                                    Usar este valor
                                </button>
                            </div>
                        )}
                        {/* Rastro: de onde veio o número que está no campo. */}
                        {unidade?.consumo_medio_origem && (
                            <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                                Definido por {ROTULO_ORIGEM_CONSUMO[unidade.consumo_medio_origem] || unidade.consumo_medio_origem}
                                {unidade.consumo_medio_definido_por ? ` — ${unidade.consumo_medio_definido_por}` : ''}
                                {unidade.consumo_medio_definido_em ? ` em ${new Date(unidade.consumo_medio_definido_em).toLocaleDateString('pt-BR')}` : ''}
                            </p>
                        )}
                    </div>
                </div>
                <div ref={avisoTrocaRef}>
                    {ucExistente && !unidade && (
                        <AvisoUcExistente
                            uc={ucExistente}
                            pedindo={pedindoTroca}
                            justificativa={justificativaTroca}
                            onPedir={() => setPedindoTroca(true)}
                            onJustificativa={setJustificativaTroca}
                            onConfirmar={pedirTrocaDeDono}
                            onCancelar={() => { setPedindoTroca(false); setJustificativaTroca(''); }}
                        />
                    )}
                </div>

                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Valor Tarifa (R$)
                        </label>
                        <input
                            type="text"
                            value={valorTarifaInput}
                            onChange={(e) => {
                                const val = e.target.value;
                                // Allow numbers, commas, dots
                                if (/^[0-9]*[.,]?[0-9]*$/.test(val)) {
                                    setValorTarifaInput(val);
                                }
                            }}
                            onBlur={() => {
                                if (valorTarifaInput) {
                                    const numVal = parseFloat(valorTarifaInput.replace(',', '.'));
                                    setFormData(prev => ({ ...prev, valor_tarifa: isNaN(numVal) ? undefined : numVal }));
                                } else {
                                    setFormData(prev => ({ ...prev, valor_tarifa: undefined }));
                                }
                            }}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white"
                            placeholder="1,125214"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Tipo de Ligação
                        </label>
                        <select
                            value={formData.tipo_ligacao || ''}
                            onChange={e => setFormData({ ...formData, tipo_ligacao: e.target.value as any })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none bg-white dark:bg-slate-800 dark:text-white"
                        >
                            <option value="">Selecione...</option>
                            <option value="Monofásico">Monofásico</option>
                            <option value="Bifásico">Bifásico</option>
                            <option value="Trifásico">Trifásico</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Grupo Tarifário
                        </label>
                        <select
                            value={formData.grupo_tarifario || ''}
                            onChange={e => setFormData({ ...formData, grupo_tarifario: e.target.value as any })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none bg-white dark:bg-slate-800 dark:text-white"
                        >
                            <option value="">Selecione...</option>
                            <optgroup label="Grupo A - Alta Tensão">
                                <option value="A1">A1 (Tensão ≥ 230 kV)</option>
                                <option value="A2">A2 (88 kV a 138 kV)</option>
                                <option value="A3">A3 (69 kV)</option>
                                <option value="A3a">A3a (30 kV a 44 kV)</option>
                                <option value="A4">A4 (2,3 kV a 25 kV)</option>
                                <option value="AS">AS (Subterrâneo, &lt; 2,3 kV)</option>
                            </optgroup>
                            <optgroup label="Grupo B - Baixa Tensão">
                                <option value="B1">B1 (Residencial)</option>
                                <option value="B2">B2 (Rural)</option>
                                <option value="B3">B3 (Demais Classes)</option>
                                <option value="B4">B4 (Iluminação Pública)</option>
                            </optgroup>
                        </select>
                    </div>
                    {canEscolherEmpresaFaturadora && (
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                                Empresa Faturadora
                            </label>
                            <select
                                value={(formData as any).empresa_parceira_id || ''}
                                // Cliente da parceria e cliente próprio da Gran são excludentes
                                // (CHECK uc_fora_parceria_sem_parceira no banco).
                                onChange={e => setFormData({
                                    ...formData,
                                    empresa_parceira_id: e.target.value || null,
                                    ...(e.target.value && formData.fora_parceria_vigor ? { fora_parceria_vigor: false } : {}),
                                } as any)}
                                className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none bg-white dark:bg-slate-800 dark:text-white"
                            >
                                <option value="">Vigor Energy (direto)</option>
                                {empresasParceiras.map(ep => (
                                    <option key={ep.id} value={ep.id}>{ep.nome}</option>
                                ))}
                            </select>
                            {!(formData as any).empresa_parceira_id && (
                                <label className="mt-2 flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!!formData.fora_parceria_vigor}
                                        onChange={e => setFormData({ ...formData, fora_parceria_vigor: e.target.checked })}
                                        className="mt-0.5 h-4 w-4 accent-secondary"
                                    />
                                    <span>
                                        <strong>Cliente próprio da Gran</strong> (fora da parceria com a Vigor). A Vigor não calcula nem emite boleto desta UC: a cobrança sai pelo Boleto avulso da Gran.
                                    </span>
                                </label>
                            )}
                        </div>
                    )}
                    {empresaParceiraIdUc ? (
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                                % da Vigor na tarifa cheia
                            </label>
                            <input
                                type="number"
                                min="0.01"
                                max="100"
                                step="0.01"
                                disabled={!canDefinirPercentualVigor}
                                value={formData.percentual_vigor ?? ''}
                                onChange={e => setFormData({
                                    ...formData,
                                    percentual_vigor: e.target.value === '' ? null : Number(e.target.value),
                                })}
                                className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-slate-900"
                                placeholder="Ex.: 65"
                            />
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                {formData.percentual_vigor != null
                                    ? `A parceira fica com o que o cliente paga acima disso. Desconto máximo ao cliente: ${(100 - Number(formData.percentual_vigor)).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%.`
                                    : 'Sem este % o boleto da UC não pode ser emitido.'}
                                {canDefinirPercentualVigor ? '' : ' Só o Financeiro ou um adm altera.'}
                            </p>
                            {unidade?.percentual_vigor_alterado_por ? (
                                <p className="mt-0.5 text-[11px] text-gray-400">
                                    Definido por {unidade.percentual_vigor_alterado_por}
                                    {unidade.percentual_vigor_alterado_em ? ` em ${new Date(unidade.percentual_vigor_alterado_em).toLocaleDateString('pt-BR')}` : ''}
                                </p>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div> {/* End Distributor Area */}

            {/* --- ÁREA DO CLIENTE --- */}
            <div className="bg-emerald-50/50 dark:bg-emerald-500/5 rounded-2xl p-5 border border-emerald-100/50 dark:border-emerald-500/10 space-y-4">
                <h4 className="uppercase font-bold text-emerald-800 dark:text-emerald-500 mb-2 flex items-center">
                    Dados do Cliente e Negociação
                </h4>

                {/* Segmento */}
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                        Segmento
                    </label>
                    <div className="relative" ref={segmentoRef}>
                        <div
                            className="flex items-center w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm cursor-pointer bg-white dark:bg-slate-800 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all"
                            onClick={() => setSegmentoOpen(v => !v)}
                        >
                            <input
                                type="text"
                                value={segmentoOpen
                                    ? segmentoSearch
                                    : (formData.segmento === 'Outro'
                                        ? `Outro: ${segmentoOutro}`
                                        : (formData.segmento || ''))}
                                onChange={e => {
                                    setSegmentoSearch(e.target.value);
                                    setSegmentoOpen(true);
                                }}
                                onFocus={() => {
                                    setSegmentoSearch('');
                                    setSegmentoOpen(true);
                                }}
                                placeholder="Digite para buscar o segmento..."
                                className="flex-1 bg-transparent outline-none text-gray-800 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500"
                                readOnly={!segmentoOpen}
                            />
                            <ChevronDown size={14} className={`text-gray-400 transition-transform ml-2 ${segmentoOpen ? 'rotate-180' : ''}`} />
                        </div>
                        {segmentoOpen && (
                            <ul className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-lg max-h-52 overflow-y-auto custom-scrollbar">
                                {SEGMENTO_OPTIONS.filter(s => s.toLowerCase().includes(segmentoSearch.toLowerCase())).length === 0 ? (
                                    <li className="px-4 py-2 text-sm text-gray-400 dark:text-slate-500">Nenhum resultado</li>
                                ) : SEGMENTO_OPTIONS.filter(s => s.toLowerCase().includes(segmentoSearch.toLowerCase())).map(opt => (
                                    <li
                                        key={opt}
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, segmento: opt }));
                                            setSegmentoSearch('');
                                            setSegmentoOpen(false);
                                            if (opt !== 'Outro') setSegmentoOutro('');
                                        }}
                                        className={`px-4 py-2 text-sm cursor-pointer hover:bg-primary/10 dark:hover:bg-primary/20 transition-colors ${formData.segmento === opt ? 'bg-primary/10 dark:bg-primary/20 font-semibold text-primary' : 'text-gray-800 dark:text-white'}`}
                                    >
                                        {opt}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {formData.segmento === 'Outro' && (
                        <input
                            type="text"
                            value={segmentoOutro}
                            onChange={e => setSegmentoOutro(e.target.value)}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white mt-2"
                            placeholder="Qual é o segmento? Ex: Lavanderia, Farmácia de Manipulação..."
                        />
                    )}
                </div>

                <fieldset disabled={cadastroTravado} className="min-w-0 space-y-4 disabled:opacity-60">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div ref={wrapperRef} className="relative">
                        <label
                            className="flex text-xs font-bold text-gray-700 dark:text-white uppercase mb-1 cursor-help items-center relative group w-fit"
                            title="Este dado será utilizado em nossas cobranças"
                        >
                            <span className="border-b border-dashed border-gray-400 dark:border-slate-500">
                                Nome do cliente / Negoc.
                            </span>
                            <span className="text-red-500 ml-1">*</span>
                            <div className="absolute opacity-0 invisible group-hover:opacity-100 group-hover:visible bg-gray-900 text-white text-[10px] rounded px-3 py-2 -top-10 left-0 whitespace-nowrap z-[120] transition-all font-normal normal-case shadow-xl">
                                Este dado será utilizado em nossas cobranças
                                <div className="absolute bottom-[-4px] left-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                            </div>
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                required
                                value={formData.nome_cliente}
                                onChange={handleClientNameChange}
                                onFocus={() => formData.nome_cliente && fetchNegotiations(formData.nome_cliente)}
                                className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all pr-10 dark:bg-slate-800 dark:text-white"
                                placeholder="Pesquisar ou adicionar cliente..."
                            />
                            <div className="absolute right-3 top-3 text-gray-400 dark:text-slate-500">
                                <Search size={16} />
                            </div>
                        </div>

                        {/* Suggestions Dropdown */}
                        {showSuggestions && (
                            <div className="absolute top-full left-0 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 rounded-xl shadow-xl z-20 overflow-hidden animate-fade-in">
                                {negotiations.length > 0 && (
                                    <div className="max-h-40 overflow-y-auto custom-scrollbar">
                                        {negotiations.map(neg => (
                                            <div
                                                key={neg.id}
                                                onClick={() => selectNegotiation(neg)}
                                                className="px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer text-sm text-gray-700 dark:text-slate-200 border-b border-gray-50 dark:border-slate-700 last:border-none"
                                            >
                                                {neg.cliente_nome}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div
                                    onClick={() => {
                                        setShowQuickCreate(true);
                                        setShowSuggestions(false);
                                    }}
                                    className="px-4 py-3 bg-gray-50 dark:bg-slate-700/50 hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer flex items-center gap-2 text-primary font-bold text-sm transition-colors border-t border-gray-100 dark:border-slate-700"
                                >
                                    <UserPlus size={16} />
                                    Criar novo cliente "{formData.nome_cliente}"
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <label
                            className="flex text-xs font-bold text-gray-700 dark:text-white uppercase mb-1 cursor-help items-center relative group w-fit"
                            title="Este dado será utilizado em nossas cobranças"
                        >
                            <span className="border-b border-dashed border-gray-400 dark:border-slate-500">
                                CPF/CNPJ do Cliente
                            </span>
                            <div className="absolute opacity-0 invisible group-hover:opacity-100 group-hover:visible bg-gray-900 text-white text-[10px] rounded px-3 py-2 -top-10 left-0 whitespace-nowrap z-[120] transition-all font-normal normal-case shadow-xl">
                                Este dado será utilizado em nossas cobranças
                                <div className="absolute bottom-[-4px] left-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                            </div>
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={formatCpfCnpj(formData.cpf_cnpj_cliente || '')}
                                onChange={(e) => {
                                    const cleanValue = e.target.value.replace(/\D/g, '');
                                    if (cleanValue.length <= 14) {
                                        setFormData({ ...formData, cpf_cnpj_cliente: cleanValue });
                                    }
                                }}
                                className={`w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 ${canConsultCpfCnpj ? 'pr-10' : ''} text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white`}
                                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                                title="Este dado será utilizado em nossas cobranças"
                            />
                            {canConsultCpfCnpj && (
                                <button
                                    type="button"
                                    onClick={() => handleAutofillCpfCnpj('cliente')}
                                    disabled={!formData.cpf_cnpj_cliente || (formData.cpf_cnpj_cliente.replace(/\D/g, '').length !== 11 && formData.cpf_cnpj_cliente.replace(/\D/g, '').length !== 14) || isFetchingCliente}
                                    className="absolute right-2 top-2 p-1 text-gray-400 hover:text-primary disabled:opacity-50 transition-colors"
                                    title="Buscar dados do CPF/CNPJ"
                                >
                                    {isFetchingCliente ? (
                                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <Search size={20} className={(!formData.cpf_cnpj_cliente || (formData.cpf_cnpj_cliente.replace(/\D/g, '').length !== 11 && formData.cpf_cnpj_cliente.replace(/\D/g, '').length !== 14)) ? 'opacity-30' : 'text-primary'} />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Data de Nascimento do Cliente
                        </label>
                        <input
                            type="date"
                            value={formData.data_nascimento_cliente || ''}
                            onChange={(e) => setFormData({ ...formData, data_nascimento_cliente: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-[9px] text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none bg-white dark:bg-slate-800 dark:text-white"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            WhatsApp do Cliente
                        </label>
                        <input
                            type="text"
                            value={formData.whatsapp_cliente || ''}
                            onChange={(e) => {
                                let val = e.target.value;
                                // Auto-format phone loosely: (XX) XXXXX-XXXX
                                const cleanVal = val.replace(/\D/g, '');
                                let formatted = cleanVal.slice(0, 11);
                                if (formatted.length > 2) formatted = `(${formatted.slice(0, 2)}) ${formatted.slice(2)}`;
                                if (cleanVal.length > 7) formatted = `${formatted.slice(0, 10)}-${formatted.slice(10)}`;

                                setFormData({ ...formData, whatsapp_cliente: formatted });
                            }}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white"
                            placeholder="(00) 00000-0000"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            E-mail do Cliente
                        </label>
                        <input
                            type="email"
                            value={formData.email_cliente || ''}
                            onChange={(e) => setFormData({ ...formData, email_cliente: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white"
                            placeholder="email@exemplo.com"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Outro e-mail
                        </label>
                        <input
                            type="email"
                            value={formData.outro_email || ''}
                            onChange={(e) => setFormData({ ...formData, outro_email: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white"
                            placeholder="outro.email@exemplo.com"
                        />
                    </div>
                </div>
                </fieldset>

                {/* Quick Create Modal */}
                <QuickCreateClientModal
                    isOpen={showQuickCreate}
                    onClose={() => setShowQuickCreate(false)}
                    initialName={formData.nome_cliente || ''}
                    onSuccess={(neg) => {
                        setFormData({ ...formData, nome_cliente: neg.cliente_nome, negociacao_id: neg.id });
                        setShowQuickCreate(false);
                    }}
                />

                {/* Address Fields */}
                <fieldset disabled={cadastroTravado} className="min-w-0 space-y-4 disabled:opacity-60">
                <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1">
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            CEP
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={formData.cep || ''}
                                onChange={(e) => setFormData({ ...formData, cep: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                                onBlur={handleSearchCEP}
                                className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white"
                                placeholder="00000000"
                            />
                            {searching && (
                                <div className="absolute right-3 top-3 animate-spin">
                                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full"></div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-span-2">
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Logradouro
                        </label>
                        <input
                            type="text"
                            value={formData.logradouro || ''}
                            onChange={e => setFormData({ ...formData, logradouro: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white"
                            placeholder="Rua, Avenida..."
                        />
                    </div>
                </div>

                {/* Número + Quadra + Lote with "Sem" checkboxes */}
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase">
                                Número
                            </label>
                            <label className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-slate-400 cursor-pointer hover:text-primary transition-colors whitespace-nowrap">
                                <input
                                    type="checkbox"
                                    checked={semNumero}
                                    onChange={(e) => {
                                        setSemNumero(e.target.checked);
                                        if (e.target.checked) {
                                            setFormData(prev => ({ ...prev, numero: 'S/N' }));
                                        } else {
                                            setFormData(prev => ({ ...prev, numero: '' }));
                                        }
                                    }}
                                    className="rounded border-gray-300 dark:border-slate-700 text-primary focus:ring-primary w-3 h-3 dark:bg-slate-800"
                                />
                                S/N
                            </label>
                        </div>
                        <input
                            type="text"
                            value={formData.numero || ''}
                            onChange={e => setFormData({ ...formData, numero: e.target.value })}
                            disabled={semNumero}
                            className={`w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all ${semNumero ? 'bg-gray-100 dark:bg-slate-800/50 text-gray-400 dark:text-slate-500 cursor-not-allowed' : 'dark:bg-slate-800 dark:text-white'}`}
                            placeholder="123"
                        />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase">
                                Quadra
                            </label>
                            <label className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-slate-400 cursor-pointer hover:text-primary transition-colors whitespace-nowrap">
                                <input
                                    type="checkbox"
                                    checked={semQuadra}
                                    onChange={(e) => {
                                        setSemQuadra(e.target.checked);
                                        if (e.target.checked) {
                                            setFormData(prev => ({ ...prev, quadra: 'Sem quadra' }));
                                        } else {
                                            setFormData(prev => ({ ...prev, quadra: '' }));
                                        }
                                    }}
                                    className="rounded border-gray-300 dark:border-slate-700 text-primary focus:ring-primary w-3 h-3 dark:bg-slate-800"
                                />
                                Não existe
                            </label>
                        </div>
                        <input
                            type="text"
                            value={formData.quadra || ''}
                            onChange={e => setFormData({ ...formData, quadra: e.target.value })}
                            disabled={semQuadra}
                            className={`w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all ${semQuadra ? 'bg-gray-100 dark:bg-slate-800/50 text-gray-400 dark:text-slate-500 cursor-not-allowed' : 'dark:bg-slate-800 dark:text-white'}`}
                            placeholder="Qd. 10"
                        />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase">
                                Lote
                            </label>
                            <label className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-slate-400 cursor-pointer hover:text-primary transition-colors whitespace-nowrap">
                                <input
                                    type="checkbox"
                                    checked={semLote}
                                    onChange={(e) => {
                                        setSemLote(e.target.checked);
                                        if (e.target.checked) {
                                            setFormData(prev => ({ ...prev, lote: 'Sem lote' }));
                                        } else {
                                            setFormData(prev => ({ ...prev, lote: '' }));
                                        }
                                    }}
                                    className="rounded border-gray-300 dark:border-slate-700 text-primary focus:ring-primary w-3 h-3 dark:bg-slate-800"
                                />
                                Não existe
                            </label>
                        </div>
                        <input
                            type="text"
                            value={formData.lote || ''}
                            onChange={e => setFormData({ ...formData, lote: e.target.value })}
                            disabled={semLote}
                            className={`w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all ${semLote ? 'bg-gray-100 dark:bg-slate-800/50 text-gray-400 dark:text-slate-500 cursor-not-allowed' : 'dark:bg-slate-800 dark:text-white'}`}
                            placeholder="Lt. 05"
                        />
                    </div>
                </div>

                {/* Compl + Bairro */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Compl.
                        </label>
                        <input
                            type="text"
                            value={formData.complemento || ''}
                            onChange={e => setFormData({ ...formData, complemento: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white"
                            placeholder="Ap 101"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Bairro
                        </label>
                        <input
                            type="text"
                            value={formData.bairro || ''}
                            onChange={e => setFormData({ ...formData, bairro: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white"
                            placeholder="Centro"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Cidade
                        </label>
                        <input
                            type="text"
                            value={formData.cidade || ''}
                            onChange={e => setFormData({ ...formData, cidade: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white"
                            placeholder="Cidade"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Estado
                        </label>
                        <input
                            type="text"
                            value={formData.estado || ''}
                            onChange={e => setFormData({ ...formData, estado: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white"
                            placeholder="UF"
                        />
                    </div>
                </div>
                </fieldset>

            </div> {/* End Client Area */}

            {/* --- ÁREA DE DETALHES DO PLANO E OBSERVAÇÕES --- */}
            <div className="bg-blue-50/50 dark:bg-blue-500/5 rounded-2xl p-5 border border-blue-100/50 dark:border-blue-500/10 space-y-4">
                <h4 className="uppercase font-bold text-blue-800 dark:text-blue-500 mb-2 flex items-center">
                    Detalhes do Plano, Status e Observações
                </h4>

                {/* Cartão da condição da UC. A matriz de multiplicadores saiu da tela:
                    ela é da comissão do vendedor externo, e aqui o que importa é o
                    desconto do cliente. O multiplicador virou "ver detalhes" — por
                    modalidade, porque o modelo do Comercial Interno e o do parceiro
                    não são a matriz do externo. */}
                {/* O link "Cliente precisa de desconto acima da política?" (CondicaoEspecialFields)
                    saiu daqui: o ajuste fino do cartão chega a qualquer valor, e o cartão já
                    avisa quando sai da política. Com negociação, a aprovação dispara ao salvar. */}
                <PlanoResumoCard
                    modalidade={modalidadeVenda}
                    modoCriacao={!unidade}
                    empresaParceiraId={empresaParceiraIdUc}
                    empresaParceiraNome={empresasParceiras.find(ep => ep.id === empresaParceiraIdUc)?.nome ?? null}
                    empresasParceiras={canEscolherEmpresaFaturadora ? empresasParceiras : undefined}
                    onChangeEmpresaParceira={canEscolherEmpresaFaturadora
                        ? id => setFormData(prev => ({ ...prev, empresa_parceira_id: id } as any))
                        : undefined}
                    onChangeFidelidade={negociacaoVinculadaId
                        ? undefined
                        : f => setFormData(prev => ({ ...prev, fidelidade: f }))}
                    descTarifa={formData.desconto_tarifa}
                    descBandeira={formData.desconto_bandeira}
                    onChangeDescTarifa={v => { setForaDaPolitica(false); setFormData(prev => ({ ...prev, desconto_tarifa: v })); }}
                    onChangeDescBandeira={v => { setForaDaPolitica(false); setFormData(prev => ({ ...prev, desconto_bandeira: v })); }}
                    volumeKwh={volumeOutrasUcs + (consumoInput ? (parseFloat(consumoInput.replace(',', '.')) || 0) : (Number(formData.consumo_medio_kwh) || 0))}
                    fidelidade={negociacaoVinculadaId ? selectedNegotiationFidelity : formData.fidelidade}
                    foraSelecionado={foraDaPolitica}
                    negociacaoId={negociacaoVinculadaId}
                    onChangeModalidade={negociacaoVinculadaId
                        ? undefined
                        : m => setFormData(prev => ({ ...prev, plano_modalidade: m }))}
                    onSelectPlano={plano => {
                        setForaDaPolitica(false);
                        setFormData(prev => ({
                            ...prev,
                            desconto_tarifa: plano.descTarifa,
                            desconto_bandeira: plano.descBandeira,
                        }));
                    }}
                />

                {/* Quem mudou o desconto, quando e se foi só numa fatura — ver
                    src/lib/descontoHistorico.ts. */}
                {unidade?.id ? <HistoricoDescontoUc ucId={unidade.id} /> : null}

                <div className="hidden">
                    <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                        Carência
                    </label>
                    <select
                        value={formData.carencia || ''}
                        onChange={e => setFormData({ ...formData, carencia: e.target.value })}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none bg-white dark:bg-slate-800 dark:text-white"
                    >
                        <option value="">Selecione...</option>
                        <option value="até 30 dias (1 mês)">até 30 dias (1 mês)</option>
                        <option value="até 60 dias (2 meses)">até 60 dias (2 meses)</option>
                        <option value="sem carência">sem carência</option>
                    </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Situação
                        </label>
                        <select
                            value={formData.situacao?.trim() || ''}
                            onChange={e => setFormData({ ...formData, situacao: (e.target.value || null) as any })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none bg-white dark:bg-slate-800 dark:text-white"
                        >
                            <option value="">— Não definida (aguardando rateio) —</option>
                            <option value="Ativa">Ativa</option>
                            <option value="Ativa-S/-Rateio">Ativa-S/-Rateio</option>
                            <option value="1° Ciclo">1° Ciclo</option>
                            <option value="2° Ciclo">2° Ciclo</option>
                            <option value="Cancelada">Cancelada</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                            Status Financeiro
                        </label>
                        <select
                            value={formData.status_financeiro?.trim()}
                            onChange={e => setFormData({ ...formData, status_financeiro: e.target.value as any })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none bg-white dark:bg-slate-800 dark:text-white"
                        >
                            <option value="Não se aplica">Não se aplica</option>
                            <option value="Adimplente">Adimplente</option>
                            <option value="Inadimplente">Inadimplente</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-white uppercase mb-1">
                        Observações
                    </label>
                    <textarea
                        value={formData.observacoes_uc || ''}
                        onChange={e => setFormData({ ...formData, observacoes_uc: e.target.value })}
                        rows={3}
                        className="w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all dark:bg-slate-800 dark:text-white resize-y"
                        placeholder="Adicione observações importantes sobre esta unidade consumidora..."
                    ></textarea>
                </div>
            </div> {/* End Plan Area */}

            {/* Footer Fixo */}
            <div className="sticky bottom-0 bg-white dark:bg-gray-900 py-4 flex justify-end gap-3 border-t border-gray-200 dark:border-slate-800 z-20 -mx-6 px-6 mt-6 shadow-[0_-10px_20px_-5px_rgba(0,0,0,0.05)] dark:shadow-[0_-10px_20px_-5px_rgba(0,0,0,0.2)]">
                <button
                    type="button"
                    onClick={onClose}
                    className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary/90 transition-all shadow-lg shadow-primary/30 flex items-center gap-2 disabled:opacity-70"
                >
                    <Save size={18} />
                    {loading ? 'Salvando...' : 'Salvar Unidade'}
                </button>
            </div>
        </form>
    );
};

export default UnidadeForm;
